import { Fetcher } from '@this/src/util';
import type { FeedbackType } from '../types/messages';
import type {
  ForeignItinerary,
  HotelDetailData,
  PreferredTransportType,
  ProposalBundle,
  ProposalBundleCategory,
  ProposalBundleState,
  ProposalItem,
  SummaryItemCategory,
  TicketCategory
} from '../types/proposalBundles';

type ProposalItemResponse = {
  id: number;
  title: string | null;
  subtitle: string | null;
  departure_time: string | null;
  arrival_time: string | null;
  departure_date: string | null;
  arrival_date: string | null;
  flight_name: string | null;
  is_connection_flight: boolean;
  connection_summary: string | null;
  seat_class: string | null;
  ticket_type: string | null;
  foreign_itinerary: ForeignItinerary | null;
  ticket_category: TicketCategory | null;
  price: number | null;
  price_with_tax: number | null;
  price_text: string | null;
  ai_highlight_tags: string[];
  is_air: boolean | null;
  is_shinkansen: boolean | null;
  stay_days: number | null;
  breakfast_exist: boolean | null;
  smoke: boolean | null;
  selected: boolean;
  segment_route_label: string | null;
  airline_display: string | null;
  round_trip_duration_label: string | null;
  leg_duration_summary: string | null;
  round_trip_flight_id: string | null;
  foreign_round_trip_display_key: string;
};

type ProposalBundleResponse = {
  id: number;
  category: ProposalBundleCategory;
  state: ProposalBundleState;
  origin: string | null;
  destination: string | null;
  departure_date: string | null;
  return_date: string | null;
  stay_days: number | null;
  preferred_transport_type: PreferredTransportType | null;
  has_foreign_flight_round_trip: boolean | null;
  has_foreign_flight: boolean | null;
  feedback?: FeedbackType | null;
  feedback_comment_submitted?: boolean;
  items: ProposalItemResponse[];
};

type FetchProposalBundlesResponse = {
  bundles: ProposalBundleResponse[];
};

const toProposalItem = (item: ProposalItemResponse): ProposalItem => ({
  id: item.id,
  title: item.title,
  subtitle: item.subtitle,
  departureTime: item.departure_time,
  arrivalTime: item.arrival_time,
  departureDate: item.departure_date ?? null,
  arrivalDate: item.arrival_date ?? null,
  flightName: item.flight_name,
  isConnectionFlight: item.is_connection_flight,
  connectionSummary: item.connection_summary,
  seatClass: item.seat_class,
  ticketType: item.ticket_type,
  foreignItinerary: item.foreign_itinerary ?? null,
  ticketCategory: item.ticket_category,
  price: item.price,
  priceWithTax: item.price_with_tax,
  priceText: item.price_text,
  aiHighlightTags: item.ai_highlight_tags,
  isAir: item.is_air,
  isShinkansen: item.is_shinkansen,
  stayDays: item.stay_days,
  breakfastExist: item.breakfast_exist,
  smoke: item.smoke,
  selected: item.selected,
  segmentRouteLabel: item.segment_route_label ?? null,
  airlineDisplay: item.airline_display ?? null,
  roundTripDurationLabel: item.round_trip_duration_label ?? null,
  legDurationSummary: item.leg_duration_summary ?? null,
  roundTripFlightId: item.round_trip_flight_id != null ? String(item.round_trip_flight_id) : null,
  foreignRoundTripDisplayKey: item.foreign_round_trip_display_key
});

const toProposalBundle = (bundle: ProposalBundleResponse): ProposalBundle => ({
  id: bundle.id,
  category: bundle.category,
  state: bundle.state,
  origin: bundle.origin,
  destination: bundle.destination,
  departureDate: bundle.departure_date,
  returnDate: bundle.return_date,
  stayDays: bundle.stay_days,
  preferredTransportType: bundle.preferred_transport_type,
  hasForeignFlightRoundTrip: bundle.has_foreign_flight_round_trip ?? null,
  hasForeignFlight: bundle.has_foreign_flight ?? null,
  feedback: bundle.feedback ?? null,
  feedbackCommentSubmitted: bundle.feedback_comment_submitted ?? false,
  items: bundle.items.map(toProposalItem)
});

export const fetchProposalBundles = async (tripId: string): Promise<ProposalBundle[]> => {
  const response = await Fetcher.get<FetchProposalBundlesResponse>(`/trips/${tripId}/ai_proposal_bundles.json`);
  return response.bundles.map(toProposalBundle);
};

type RejectProposalBundleResponse = {
  success: boolean;
  bundle_id: number;
  state: string;
};

type UpdateProposalBundleFeedbackResponseArgs = {
  id: number;
  feedback: FeedbackType;
  feedback_comment_submitted: boolean;
};

export const updateProposalBundleFeedback = async (
  tripId: string,
  bundleId: number,
  feedbackType: FeedbackType,
  feedbackComment?: string
): Promise<{ id: number; feedback: FeedbackType; feedbackCommentSubmitted: boolean }> => {
  const response = await Fetcher.patch<UpdateProposalBundleFeedbackResponseArgs>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/feedback.json`,
    {
      feedback_type: feedbackType,
      feedback_comment: feedbackComment
    }
  );
  return {
    id: response.id,
    feedback: response.feedback,
    feedbackCommentSubmitted: response.feedback_comment_submitted
  };
};

export const rejectProposalBundle = async (tripId: string, bundleId: number): Promise<{ success: boolean }> => {
  const response = await Fetcher.patch<RejectProposalBundleResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/reject.json`
  );
  return { success: response.success };
};

type SelectProposalItemResponse = {
  success: boolean;
  bundle_id: number;
  item_id: number;
  state: ProposalBundleState;
};

export const selectProposalItem = async (
  tripId: string,
  bundleId: number,
  itemId: number
): Promise<{ success: boolean; state: ProposalBundleState }> => {
  const response = await Fetcher.patch<SelectProposalItemResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/items/${itemId}/select.json`
  );
  return { success: response.success, state: response.state };
};

type SummaryItemResponse = {
  category: SummaryItemCategory;
  label: string;
  text: string;
  price: number | null;
  price_with_tax: number | null;
};

type SummaryResponse = {
  items: SummaryItemResponse[];
  total_price: number;
  total_price_with_tax: number;
  has_selection: boolean;
};

type CompleteProposalSelectionResponse = {
  success: boolean;
  message_id: number | null;
  summary: SummaryResponse | null;
  has_selection: boolean;
  already_completed: boolean;
};

export const completeProposalSelection = async (
  tripId: string
): Promise<{ success: boolean; alreadyCompleted: boolean }> => {
  const response = await Fetcher.post<CompleteProposalSelectionResponse>(
    `/trips/${tripId}/ai_proposal_bundles/complete.json`,
    {}
  );
  return { success: response.success, alreadyCompleted: response.already_completed };
};

type HotelDetailResponse = {
  hotel: {
    name: string;
    address: string;
    images: string[];
    latitude: number;
    longitude: number;
    distance: number;
    nearest_station_name: string | null;
    station_distance: number;
    checkin: string;
    checkout: string;
    tel: string;
    cancel_policy: string;
    refundable: boolean | null;
    property_amenities: string[] | null;
    room_amenities: string[] | null;
    note: string | null;
    parking_information: string | null;
    checkin_instructions: string | null;
    special_checkin_instructions: string | null;
  };
  dest_location: { lat: number; lng: number } | null;
  plan: {
    room_name: string;
    plan_name: string | null;
    plan_detail: string | null;
    price: number;
    price_with_tax: number;
    price_text: string;
    smoke: boolean | null;
    bed_types: { description: string }[] | null;
    checkin_time: string | null;
  };
};

const toHotelDetailData = (response: HotelDetailResponse): HotelDetailData => ({
  hotel: {
    name: response.hotel.name,
    address: response.hotel.address,
    images: response.hotel.images,
    latitude: response.hotel.latitude,
    longitude: response.hotel.longitude,
    distance: response.hotel.distance,
    nearestStationName: response.hotel.nearest_station_name,
    stationDistance: response.hotel.station_distance,
    checkin: response.hotel.checkin,
    checkout: response.hotel.checkout,
    tel: response.hotel.tel,
    cancelPolicy: response.hotel.cancel_policy,
    refundable: response.hotel.refundable,
    propertyAmenities: response.hotel.property_amenities,
    roomAmenities: response.hotel.room_amenities,
    note: response.hotel.note,
    parkingInformation: response.hotel.parking_information,
    checkinInstructions: response.hotel.checkin_instructions,
    specialCheckinInstructions: response.hotel.special_checkin_instructions
  },
  destLocation: response.dest_location,
  plan: {
    roomName: response.plan.room_name,
    planName: response.plan.plan_name,
    planDetail: response.plan.plan_detail,
    price: response.plan.price,
    priceWithTax: response.plan.price_with_tax,
    priceText: response.plan.price_text,
    smoke: response.plan.smoke,
    bedTypes: response.plan.bed_types,
    checkinTime: response.plan.checkin_time
  }
});

export const fetchProposalItemHotelDetail = async (
  tripId: string,
  itemId: number,
  signal?: AbortSignal
): Promise<HotelDetailData> => {
  const response = await Fetcher.get<HotelDetailResponse>(
    `/trips/${tripId}/ai_proposal_items/${itemId}/hotel_detail.json`,
    undefined,
    undefined,
    signal
  );
  return toHotelDetailData(response);
};

type GoogleReviewResponse = {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
};

type GoogleReviewsResponse = {
  rating: number | null;
  user_ratings_total: number | null;
  reviews: GoogleReviewResponse[] | null;
};

type ProposalItemGoogleReviewsResponse = {
  google_reviews: GoogleReviewsResponse | null;
};

export type GoogleReview = {
  authorName: string;
  rating: number;
  relativeTimeDescription: string;
  text: string;
};

export type GoogleReviews = {
  rating: number | null;
  userRatingsTotal: number | null;
  reviews: GoogleReview[];
};

const toGoogleReviews = (response: GoogleReviewsResponse | null): GoogleReviews => {
  if (response == null) {
    return {
      rating: null,
      userRatingsTotal: null,
      reviews: []
    };
  }

  return {
    rating: response.rating,
    userRatingsTotal: response.user_ratings_total,
    reviews: (response.reviews || []).map(review => ({
      authorName: review.author_name,
      rating: review.rating,
      relativeTimeDescription: review.relative_time_description,
      text: review.text
    }))
  };
};

export const fetchProposalItemGoogleReviews = async (
  tripId: string,
  itemId: number,
  signal?: AbortSignal
): Promise<GoogleReviews> => {
  const response = await Fetcher.get<ProposalItemGoogleReviewsResponse>(
    `/trips/${tripId}/ai_proposal_items/${itemId}/google_reviews.json`,
    undefined,
    undefined,
    signal
  );
  return toGoogleReviews(response.google_reviews);
};

export type HotelProposalReasonStatus = 'completed' | 'unavailable';

export type HotelProposalReason = {
  itemId: number;
  reason: string;
};

export type HotelProposalReasonsResult = {
  bundleId: number;
  status: HotelProposalReasonStatus;
  summaryReason: string | null;
  itemReasons: HotelProposalReason[];
};

type HotelProposalReasonResponse = {
  item_id: number;
  reason: string;
};

type HotelProposalReasonsResponse = {
  bundle_id: number;
  status: HotelProposalReasonStatus;
  summary_reason: string | null;
  item_reasons: HotelProposalReasonResponse[];
};

const toHotelProposalReasonsResult = (response: HotelProposalReasonsResponse): HotelProposalReasonsResult => ({
  bundleId: response.bundle_id,
  status: response.status,
  summaryReason: response.summary_reason,
  itemReasons: response.item_reasons.map(entry => ({
    itemId: entry.item_id,
    reason: entry.reason
  }))
});

export const fetchHotelProposalReasons = async (
  tripId: string,
  bundleId: number
): Promise<HotelProposalReasonsResult> => {
  const response = await Fetcher.get<HotelProposalReasonsResponse>(
    `/trips/${tripId}/ai_proposal_bundles/${bundleId}/hotel_reasons.json`
  );
  return toHotelProposalReasonsResult(response);
};
